import { overviewView, periodDates, placementView } from './topPlacementOverview';

export const blankPeriod = () => ({ clinic_id: null, starts_on: '', ends_on: '', weight: 0,
  payment_reference: '', all_directions: true, illness_kind_ids: [], show_on_homepage: false });

export function periodForm(row) {
  const dates = periodDates(row);
  return { clinic_id: row.clinic_id, starts_on: dates.from, ends_on: dates.to, weight: row.weight,
    payment_reference: row.payment_reference || '', all_directions: row.illness_kind_ids.length === 0,
    illness_kind_ids: [...row.illness_kind_ids], show_on_homepage: row.show_on_homepage };
}

const validDay = value => /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(value + 'T12:00:00Z')) && new Date(value + 'T12:00:00Z').toISOString().slice(0, 10) === value;

export function formError(form, creating = false) {
  if (!Number.isInteger(form.clinic_id) || form.clinic_id < 1) return 'Choose a clinic.';
  if (!validDay(form.starts_on) || !validDay(form.ends_on) || form.ends_on < form.starts_on) return 'Choose a valid first and last day.';
  if (!Number.isInteger(form.weight) || form.weight < 0 || form.weight > 100) return 'Weight must be a whole number from 0 to 100.';
  if (typeof form.all_directions !== 'boolean' || typeof form.show_on_homepage !== 'boolean' || !Array.isArray(form.illness_kind_ids)) return 'Choose valid display settings.';
  if (creating && !form.payment_reference.trim()) return 'Enter a payment reference.';
  if (!form.all_directions && (!form.illness_kind_ids.length || form.illness_kind_ids.some(id => !Number.isInteger(id) || id < 1))) {
    return 'Choose at least one direction, or select All directions.';
  }
  return '';
}

const directions = form => form.all_directions ? [] : [...new Set(form.illness_kind_ids)].sort((a, b) => a - b);
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export function editPayload(row, form) {
  const error = formError(form);
  if (error) throw new Error(error);
  const current = periodForm(row);
  const data = { starts_on: form.starts_on, ends_on: form.ends_on, sort_order: row.sort_order };
  if (row.weight !== form.weight) data.weight = form.weight;
  if (!same(row.illness_kind_ids, directions(form))) data.illness_kind_ids = directions(form);
  if (row.show_on_homepage !== form.show_on_homepage) data.show_on_homepage = form.show_on_homepage;
  if (current.starts_on === form.starts_on && current.ends_on === form.ends_on && Object.keys(data).length === 3) return null;
  return data;
}

export function availableActions(row, now = Date.now()) {
  if (!row || row.status === 'cancelled') return [];
  const actions = [];
  if (row.status === 'draft' && Date.parse(row.ends_at) > now) actions.push('confirm');
  if (row.status === 'confirmed') actions.push('pause');
  if (row.status === 'paused' && Date.parse(row.ends_at) > now) actions.push('resume');
  return [...actions, 'cancel'];
}

// Compare only owner business fields, not labels or the form. No metadata writer can authorize an action.
export const placementFingerprint = row => JSON.stringify([
  row.placement_id, row.clinic_id, row.starts_at, row.ends_at, row.sort_order, row.weight,
  row.status, row.illness_kind_ids, row.show_on_homepage, row.payment_reference, row.confirmed_at, row.confirmed_by,
]);

export async function readPeriod(api, row) {
  const dates = periodDates(placementView(row));
  const { data } = await api.getTopPlacementRegistry({ clinic_id: row.clinic_id, from: dates.from, to: dates.to, limit: 100 });
  const view = overviewView(data);
  const found = view.rows.find(item => item.placement_id === row.placement_id && item.clinic_id === row.clinic_id);
  if (!found) throw new Error('This period could not be found in the current data. Refresh the registry and open it again.');
  return found;
}

const errors = {
  'top_placement.slot_limit_exceeded': 'No free slot in this date range. Adjust the dates or directions.',
  'top_placement.overlapping_period': 'This clinic already has a period overlapping these dates.',
  'top_placement.version_conflict': 'This period changed in another session. Reload it before trying again.',
  'top_placement.invalid_transition': 'This action is no longer available. Reload the period.',
  'top_placement.period_finished': 'This period has ended. Create a new period to continue.',
};
export function mutationError(error) {
  const serverError = error?.response?.data?.error;
  const code = serverError?.code || (typeof serverError === 'string' ? serverError : '');
  if (code) return errors[code] || 'Request refused: ' + code;
  if (error?.response?.status === 403) return 'You do not have permission to change placements.';
  return error?.message || 'The request failed. Reload the period before trying again.';
}

function requireReceipt(response) {
  const error = response?.data?.error;
  if (error) {
    const code = error.code || String(error);
    throw new Error(errors[code] || 'Request refused: ' + code);
  }
  if (response?.data?.status !== true) throw new Error('The server did not confirm the change. Reload before trying again.');
}

// One effect boundary for the new editor: fresh owner read → conflict check → command → receipt → readback.
export async function changePeriod(api, baseline, form, action, onWrite = () => {}) {
  baseline = placementView(baseline);
  form = { ...form, illness_kind_ids: [...form.illness_kind_ids] };
  const fresh = await readPeriod(api, baseline);
  if (placementFingerprint(fresh) !== placementFingerprint(baseline)) {
    throw new Error('This period changed in another session. Reload it to review the current values.');
  }
  if (fresh.status === 'cancelled') throw new Error('Cancelled periods cannot be changed.');
  let data;
  if (action === 'save') {
    data = editPayload(fresh, form);
    if (!data) return fresh;
  } else if (!availableActions(fresh).includes(action)) {
    throw new Error('This action is no longer available. Reload the period.');
  }
  onWrite();
  requireReceipt(await (action === 'save'
    ? api.rescheduleTopPlacement({ placementId: fresh.placement_id, data })
    : api.changeTopPlacement({ placementId: fresh.placement_id, action })));
  // If dates moved, use the new window for verification, retaining the same canonical ID.
  const next = action === 'save' ? await readWindow(api, fresh.clinic_id, form.starts_on, form.ends_on) : null;
  const verified = next ? next.rows.find(row => row.placement_id === fresh.placement_id) : await readPeriod(api, fresh);
  if (!verified) throw new Error('The change was accepted, but the period could not be verified. Reload the registry.');
  const expectedStatus = { confirm: 'confirmed', pause: 'paused', resume: 'confirmed', cancel: 'cancelled' }[action];
  const matches = action === 'save' ? editPayload(verified, form) === null && verified.status === fresh.status : verified.status === expectedStatus;
  if (!matches) throw new Error('The current period differs from the requested change. Reload it before continuing.');
  return verified;
}

async function readWindow(api, clinicId, from, to) {
  const { data } = await api.getTopPlacementRegistry({ clinic_id: clinicId, from, to, limit: 100 });
  return overviewView(data);
}

export async function createPeriod(api, form, onWrite = () => {}) {
  const error = formError(form, true);
  if (error) throw new Error(error);
  form = { ...form, illness_kind_ids: [...form.illness_kind_ids] };
  const view = await readWindow(api, form.clinic_id, form.starts_on, form.ends_on);
  const exact = row => row.clinic_id === form.clinic_id && periodDates(row).from === form.starts_on && periodDates(row).to === form.ends_on;
  if (view.hasMore) throw new Error('Too many overlapping periods to verify this request. Narrow the date range.');
  if (view.rows.some(exact)) throw new Error('A period with these dates already exists. Open it in the registry.');
  onWrite();
  requireReceipt(await api.createTopPlacement({ clinicId: form.clinic_id, data: {
    starts_on: form.starts_on, ends_on: form.ends_on, sort_order: 0, weight: form.weight,
    payment_reference: form.payment_reference.trim(), illness_kind_ids: directions(form), show_on_homepage: form.show_on_homepage,
  } }));
  const after = await readWindow(api, form.clinic_id, form.starts_on, form.ends_on);
  const verified = after.rows.find(exact);
  if (!verified || verified.status !== 'draft' || editPayload(verified, form) !== null || verified.payment_reference !== form.payment_reference.trim()) {
    throw new Error('The request was accepted, but the draft could not be verified. Refresh the registry before creating another period.');
  }
  return verified;
}
